import React, { useState, useCallback, useEffect, useRef, SyntheticEvent, ReactElement } from 'react';
import styled from 'styled-components';
import { TypeTable, Icon, MultilineContent, FilterBar, IFilterDatePicker, DateInterval, IFilterItem, IFilterResult} from 'scorer-ui-kit';
import { useLocation, useParams } from 'react-router-dom';
import { ITableColumnConfig, ITypeTableData, IRowData } from 'scorer-ui-kit/dist/Tables';
import { useMovieList } from '../hooks/useMovieList';
import Pagination from '../components/Pagination';
import { useTranslation } from 'react-i18next';
import { emptyTable, formatDate} from '../util';
import i18n from 'i18next';
import {addSeconds, format, startOfDay } from 'date-fns';

const SortLabel = styled.label`
  font-family: Lato;
  font-size: 14px;
  font-weight: 500;
  color: #61696f;
  margin-right: 10px;
`;

const SortAttributeLabel = styled.label`
  font-family: Lato;
  font-size: 14px;
  font-weight: bold;
  color: #61696f;
  padding-left: 4px;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Common = styled.div`
   display: flex;
  justify-content: space-between;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const HiddenVideo = styled.video`
  display: none;
`;

const ActionColumnContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const IconWrapper = styled.div`
  cursor: pointer;
`;

const FilterButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 43px;
`;

const SortingWrapper = styled.div`
  height: 128px;
  padding-top: 37px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  position: 'relative';
`;

const FilterResultsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 60px;
`;

interface Params {
  lid: string
}

const MovieDuration: React.FC<{src: string}> = ({src}) => {
  const [duration, setDuration] = useState<number|string>('-');
  const onLoaded = useCallback(({target}: SyntheticEvent<HTMLVideoElement>)=>{
    const {duration = null} = target as HTMLVideoElement;

    if(!duration) return;
    const d = format(addSeconds(startOfDay(new Date()), duration), 'HH:mm:ss');
    setDuration(d);
  },[]);

  return (
    <>
      <div>{duration}</div>
      <HiddenVideo preload='metadata' onLoadedMetadata={onLoaded} src={src} />
    </>
  );
};

const defaultFilterState: IAppFilter = {
  startDateSF: '',
  endDateSF: '',
  sortBy: '',
  pageSize: '',
  paramsLoaded: '',
  datePickerForRuntime: {start: new Date(), end: new Date()},
};

interface IProps {
  cameraType: string;
  sfDates: any;
  getSFDates: (data: any) => void;
}

interface IAppFilter {
  startDateSF: string;
  endDateSF: string;
  pageSize: string;
  sortBy: string;
  paramsLoaded: string;
  datePickerForRuntime?: IInterval;
}

interface INewParams {
  sortBy?: string | null;
  pageSize?: string | null;
  startDateSF?: string | null;
  endDateSF?: string | null;
  selectedTab?:string | null; 
}

interface IFilterData {
  paramsLoaded: string;
  pageSize: string;
  sortBy:string;
  startDateSF: string;
  endDateSF: string;
  datePickerForRuntime: IInterval;
}

interface IInterval {
  start?: Date | null | string,
  end?: Date | null | string
}

const FILTER_DATA: IFilterData = {pageSize: '', sortBy: '', paramsLoaded: '', endDateSF: '', startDateSF: '', datePickerForRuntime: {} };

function SourceFilesTab({cameraType, getSFDates, sfDates}: IProps) {
  const [rowData, setRowData] = useState<ITypeTableData>([]);
  const { lid }: Params = useParams();
  const { state: { movieList, loading }, actions: { fetchMovieList } } = useMovieList(lid);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation(['CameraDetails', 'Common']);
  const dataRef = useRef(rowData);
  dataRef.current = rowData;
  const dateRef = useRef<HTMLDivElement>(null);
  const [filterValues, updateFilter] = useState<IAppFilter>(defaultFilterState);
  const params = useLocation().search;
  const [historyParams] = useState(params);
  const sizeOptions = [10, 20, 50, 100];

  const fetchHistoryParams = useCallback(() => {
    const params = new URLSearchParams(historyParams);
    const newParams: INewParams = {};
    newParams.pageSize = params.get('pageSize');
    newParams.startDateSF = params.get('startDateSF');
    newParams.endDateSF = params.get('endDateSF');

    const searchFilter: any = { ...FILTER_DATA };
    if(newParams.pageSize){
      setPageSize(prev => (newParams.pageSize ? JSON.parse(newParams.pageSize) : prev));
    }
    if(sfDates) {
      if(sfDates.startDateSF !== 'null') {
        searchFilter.startDateSF = format(new Date(sfDates.startDateSF), 'yyyy-MM-dd HH:mm:ss');
        searchFilter.endDateSF = format(new Date(sfDates.endDateSF), 'yyyy-MM-dd HH:mm:ss');
      }
    }
    searchFilter.paramsLoaded = 'true';
    updateFilter(searchFilter);
  }, [historyParams, sfDates]);

  useEffect(() => {
    fetchHistoryParams();
  }, [fetchHistoryParams]);

  const FilesTableColumns: ITableColumnConfig[] = [
    {
      header: t('sourceFilesTab.recordingTime'),
      sortable: false,
      cellStyle: 'firstColumn',
      showUnit: true,
      minWidth: 200
    },
    {
      header: t('sourceFilesTab.videoLength'),
      sortable: false,
      cellStyle: 'firstColumn',
      minWidth: 210,
    },
    {
      header: t('actions'),
      sortable: false,
      cellStyle: 'normalImportance',
      alignment: 'center',
      minWidth: 40
    }
  ];

  const dateConfig:IFilterDatePicker[] = [
    {
      buttonIcon: 'DateTime',
      buttonText: t('recordingDateTime'),
      dateMode: 'interval',
      id: 'datePickerForRuntime',
      timeMode: 'interval',
      lang: i18n.language === 'ja' ? 'ja' : 'en',
      timeZoneTitle: t('Common:timeZoneTitle'),
      dateTimeTextUpper: t('Common:from'),
      dateTimeTextLower: t('Common:to'),
      selected:filterValues.startDateSF !== '' ? {start: new Date(filterValues.startDateSF), end: new Date(filterValues.endDateSF)} : null
    }
  ];

  const generateActionColumn = useCallback((lid: string, signedMovieUrl: string, dateTime: string): ReactElement[] => {
    return (
      [
        <ActionColumnContainer key={lid}>
          <IconWrapper title={t('sourceFilesTab.download')} onClick={() => clickLink(signedMovieUrl)}>
            <Icon icon='DownloadVideo' color='dimmed' size={20} />
          </IconWrapper>
          <IconWrapper style={{'marginLeft': '15px'}} title={t('sourceFilesTab.analyzeThisVideo')} onClick={() => clickLink(`/cameras/${lid}/analysis/create?initialTime=${encodeURIComponent(dateTime)}`)}>
            <Icon icon='Analyse' color='primary' size={20} weight='heavy' />
          </IconWrapper>
        </ActionColumnContainer>
      ]
    );
  }, [t]);

  const generateRowData = useCallback((): ITypeTableData => {
    const data: ITypeTableData = [];
    movieList.forEach(({ RecordID, DateTime, SignedImageUrl, SignedMovieUrl }) => {
      const row: IRowData = {
        id: RecordID,
        header: {
          image: SignedImageUrl,
          mediaType: 'video',
          mediaUrl: SignedMovieUrl
        },
        columns: [
          { text: formatDate(DateTime), unit: ' JST' },
          { customComponent: <MovieDuration src={SignedMovieUrl} /> },
          { customComponent: <MultilineContent contentArray={generateActionColumn(lid, SignedMovieUrl, DateTime)} /> }
        ]
      };
      data.push(row);
    });
    return data;

  }, [lid, movieList, generateActionColumn]);


  useEffect(() => {
    setRowData(generateRowData);
  }, [generateRowData]);

  useEffect(() => {
    if(filterValues.paramsLoaded === 'true') {
      const startDate = filterValues.startDateSF ? format(new Date(filterValues.startDateSF), 'yyyy-MM-dd HH:mm:ss') : '';
      const endDate = filterValues.endDateSF ? format(new Date(filterValues.endDateSF), 'yyyy-MM-dd HH:mm:ss') : '';
      fetchMovieList({pageSize, startDate, endDate});
    }
  }, [fetchMovieList,pageSize, filterValues]);

  const onSizePerPageChange = useCallback((size: number) => {
    setPageSize(size);
  }, [setPageSize]);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  const toggleAllCallback = useCallback((checked: boolean) => {
    const newRows = [...dataRef.current];
    newRows.forEach((row) => {
      row._checked = checked;
    });
    setRowData(newRows);
  }, [setRowData]);

  const selectCallback = useCallback((checked: boolean, id?: string|number) => {
    const newRows = [...rowData];
    const targetRowIndex = newRows.findIndex(row => row.id === id);
    newRows[targetRowIndex]._checked = checked;
    setRowData(newRows);
  }, [rowData, setRowData]);

  const handleFilter = useCallback((res: IFilterResult[]) => { 
    const searchFilter:any = {...defaultFilterState};
    res.forEach((item) => {
      if(item.id === 'datePickerForRuntime') {
        searchFilter[item.id] = item.selected as DateInterval;
        searchFilter.startDateSF = searchFilter[item.id]?.start?.toString() || '';
        searchFilter.endDateSF = searchFilter[item.id]?.end?.toString() || '';
        getSFDates({startDateSF: format(new Date(searchFilter.startDateSF), 'yyyy-MM-dd HH:mm:ss'), endDateSF: format(new Date(searchFilter.endDateSF), 'yyyy-MM-dd HH:mm:ss')});
      } else {
        searchFilter[item.id] = (item.selected as IFilterItem).value as string;  
      }
    });
    if(searchFilter.startDateSF === '') {
      getSFDates(null);
    }
    updateFilter(searchFilter);
    setCurrentPage(1);
  },[getSFDates]);

  return (
    <>

      <FilterButtonContainer>
        {(cameraType && cameraType !== 'folder' && filterValues.paramsLoaded === 'true') ?
          <FilterContainer ref={dateRef}>
            <FilterBar filtersTitle={t('Common:filters')+':'} resultTextTemplate={t('Common:showingResults') + t(' ({X})').replace('{X}', '' + rowData.length) + ':'} totalResults={rowData.length} resultsDateFormat='yyyy/MM/dd - HH:mm' clearText={t('Common:clearAll')} datePickersConfig={dateConfig} onChangeCallback={handleFilter} />
          </FilterContainer>: <div />}
        <SortingWrapper>
          <Common>
            <SortLabel>{t('Common:sortedBy')}</SortLabel>
            <SortAttributeLabel>{t('sourceFilesTab.recordingTime')}</SortAttributeLabel>
          </Common>
        </SortingWrapper>
      </FilterButtonContainer>
      <FilterResultsContainer />

      <TypeTable
        columnConfig={FilesTableColumns}
        rows={(!loading && movieList.length === 0) ? emptyTable : rowData}
        toggleAllCallback={(value:any) => toggleAllCallback(value)}
        selectCallback={selectCallback}
        isLoading={loading}
        loadingText=''
        hasTypeIcon
        hasThumbnail
      />
      <PaginationContainer>
        <Pagination
          pageSizeOptions={sizeOptions}
          totalPages={5}
          defaultPage={currentPage}
          defaultPageSize={pageSize}
          onPageSizeChange={onSizePerPageChange}
          onPageChange={onPageChange}
          pageSizeText={t('Common:pagination.itemsPerPage')}
          prevPageText='<'
          nextPageText='>'
          firstPageText='<<'
          lastPageText='>>'
        />
      </PaginationContainer>
    </>
  );
}

export default SourceFilesTab;

const clickLink = (link: string) => {
  const anchor = document.createElement('a');
  anchor.href = link;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};